import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Users as UsersIcon } from "lucide-react";

/* ==================== INVESTORS ==================== */
export function InvestorsList() {
    return <PartyList kind="investors" title="Investors" testid="page-investors" />;
}
export function LendersList() {
    return <PartyList kind="lenders" title="Lenders" testid="page-lenders" />;
}

function PartyList({ kind, title, testid }) {
    const [rows, setRows] = useState([]);
    const [adding, setAdding] = useState(false);

    const load = async () => {
        const { data } = await api.get(`/${kind}`);
        setRows(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

    const del = async (r) => {
        if (!window.confirm(`Delete ${r.name}?`)) return;
        try {
            await api.delete(`/${kind}/${r.id}`);
            load();
            toast.success("Deleted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="p-6 md:p-10" data-testid={testid}>
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="chip chip-navy capitalize">Business · {kind}</div>
                    <h1 className="mt-3 font-display text-4xl tracking-tight leading-none italic">{title}</h1>
                    <div className="mt-2 text-sm text-muted-foreground">Track {kind} across all your projects and their contributions/distributions.</div>
                </div>
                <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90" data-testid={`add-${kind}-btn`}>
                    <Plus size={14} /> New {kind === "investors" ? "investor" : "lender"}
                </button>
            </div>

            <div className="mt-6 border border-border" data-testid={`${kind}-table`}>
                <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "2fr 1fr 1.5fr 1fr 60px" }}>
                    <div>Name</div><div>Type</div><div>Email</div><div>{kind === "investors" ? "KYC" : "Lender type"}</div><div></div>
                </div>
                {rows.map((r) => (
                    <div key={r.id} className="grid px-4 py-3 border-b border-border last:border-b-0 text-sm items-center" style={{ gridTemplateColumns: "2fr 1fr 1.5fr 1fr 60px" }} data-testid={`${kind}-row-${r.id}`}>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{r.entity_type}</div>
                        <div className="text-xs">{r.primary_contact_email || "—"}</div>
                        <div className="text-xs capitalize">{kind === "investors" ? (r.kyc_status || "—") : (r.lender_type || "—")}</div>
                        <button onClick={() => del(r)} className="text-destructive text-xs" data-testid={`delete-${kind}-${r.id}`}><Trash2 size={12} /></button>
                    </div>
                ))}
                {rows.length === 0 && (
                    <div className="p-10 text-center text-sm text-muted-foreground">
                        <UsersIcon size={24} className="mx-auto mb-2" />
                        No {kind} yet.
                    </div>
                )}
            </div>
            {adding && <PartyForm kind={kind} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
        </div>
    );
}

function PartyForm({ kind, onClose, onSaved }) {
    const initial = kind === "investors"
        ? { name: "", entity_type: "individual", primary_contact_email: "", phone: "", notes: "", kyc_status: "not_started" }
        : { name: "", lender_type: "bank", entity_type: "llc", primary_contact_email: "", phone: "", notes: "", loan_products: [] };
    const [form, setForm] = useState(initial);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/${kind}`, form);
            toast.success("Saved");
            onSaved?.();
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={`${kind}-form`}>
            <div className="bg-background border border-border w-full max-w-lg my-6" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="font-display text-xl italic">New {kind === "investors" ? "investor" : "lender"}</div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div><label className="overline block mb-1">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required data-testid={`${kind}-form-name`} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        {kind === "lenders" && (
                            <div><label className="overline block mb-1">Lender type</label><select value={form.lender_type} onChange={(e) => setForm({ ...form, lender_type: e.target.value })} className="input" data-testid="lender-form-type">{["bank", "private", "hard_money"].map((t) => <option key={t}>{t}</option>)}</select></div>
                        )}
                        <div><label className="overline block mb-1">Entity type</label><select value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className="input" data-testid={`${kind}-form-entity`}>{["individual", "llc", "partnership", "fund", "other"].map((t) => <option key={t}>{t}</option>)}</select></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="overline block mb-1">Email</label><input type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} className="input" data-testid={`${kind}-form-email`} /></div>
                        <div><label className="overline block mb-1">Phone</label><input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" data-testid={`${kind}-form-phone`} /></div>
                    </div>
                    <div><label className="overline block mb-1">Notes</label><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid={`${kind}-form-notes`} /></div>
                    <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="border border-border px-4 py-1.5 text-sm">Cancel</button><button type="submit" className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid={`${kind}-form-save`}>Save</button></div>
                </form>
            </div>
        </div>
    );
}
